/**
 * React session bridge for platform client implementations.
 *
 * @module platform/session-context
 */

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { setTelemetryTenant } from "@/telemetry/tenant-context-span-processor";
import { begin } from "@/utils/begin";

import type { ProgrammePlatformClient, SessionContext } from "./client";

export type PlatformSessionStatus = "loading" | "ready" | "error";

export interface PlatformSessionValue {
  client: ProgrammePlatformClient;
  session: SessionContext | null;
  status: PlatformSessionStatus;
  error: Error | null;
  refreshSession: () => Promise<void>;
}

export interface PlatformSessionProviderProps {
  client: ProgrammePlatformClient;
  sessionKey?: string;
  children: React.ReactNode;
}

/** A settled load, tagged with the session key it was resolved for. */
interface ResolvedSession {
  key: string | undefined;
  session: SessionContext | null;
  error: Error | null;
}

const PlatformSessionContext = React.createContext<PlatformSessionValue | null>(null);

export function PlatformSessionProvider({
  client,
  sessionKey = undefined,
  children,
}: PlatformSessionProviderProps) {
  // The outcome of a load, tagged with the key it was resolved for. `null` means
  // no load has settled for the key in force, which is what "loading" is: the
  // status below falls out of that comparison rather than being written
  // synchronously every time the key changes.
  const [resolved, setResolved] = useState<ResolvedSession | null>(null);
  const requestVersion = useRef(0);

  // Fetch and publish. Every update lands in a continuation, so the effect
  // below can call it without cascading a render before the first has painted.
  const loadSession = useCallback((): Promise<void> => {
    const request = requestVersion.current + 1;
    requestVersion.current = request;
    const key = sessionKey;

    return begin(() => client.getCurrentSession())
      .then((session) => {
        // A "ready" session must carry an authorization subject: the shell and
        // its guards read `session.subject.accountRole` on the strength of it.
        // The API always sends one, so a resolved value without a subject is a
        // broken response (an empty body, a proxy-served 200), not a usable
        // session. Treating it as an error routes it to the error screen
        // instead of letting a subjectless object crash the first render.
        if (!session?.subject) {
          throw new Error("The platform returned a session without an authorization subject.");
        }
        if (requestVersion.current === request) {
          setResolved({ key, session, error: null });
        }
      })
      .catch((cause: unknown) => {
        if (requestVersion.current === request) {
          setResolved({
            key,
            session: null,
            error: cause instanceof Error ? cause : new Error("Unable to resolve platform session"),
          });
        }
      });
  }, [client, sessionKey]);

  // An explicit re-request on an unchanged key. Dropping the outcome first is
  // what puts the shell back into loading; a key change does it on its own.
  const refreshSession = useCallback(async (): Promise<void> => {
    setResolved(null);
    await loadSession();
  }, [loadSession]);

  useEffect(() => {
    loadSession();
    return () => {
      requestVersion.current += 1;
    };
  }, [loadSession]);

  const settled = resolved !== null && resolved.key === sessionKey;
  const session = settled ? resolved.session : null;
  const error = settled ? resolved.error : null;
  let status: PlatformSessionStatus;
  if (!settled) {
    status = "loading";
  } else if (error) {
    status = "error";
  } else {
    status = "ready";
  }

  // Best-effort telemetry enrichment, not a data dependency of anything
  // here: a resolved session's tenant record is the single source for both
  // the platform tenant id/slug and the Entra directory tenant id (once
  // bound), the same way it is for every other tenant-scoped decision this
  // app makes. See src/telemetry/tenant-context-span-processor.ts.
  useEffect(() => {
    setTelemetryTenant(
      session
        ? {
            id: session.tenant.id,
            slug: session.tenant.slug,
            entraTenantId: session.tenant.entraTenantId,
          }
        : undefined,
    );
  }, [session]);

  const value = useMemo<PlatformSessionValue>(
    () => ({ client, session, status, error, refreshSession }),
    [client, error, refreshSession, session, status],
  );

  return (
    <PlatformSessionContext.Provider value={value}>{children}</PlatformSessionContext.Provider>
  );
}

export function usePlatformSession(): PlatformSessionValue {
  const value = useContext(PlatformSessionContext);
  if (!value) {
    throw new Error("usePlatformSession must be used within a <PlatformSessionProvider>");
  }
  return value;
}

export function useOptionalPlatformSession(): PlatformSessionValue | null {
  return useContext(PlatformSessionContext);
}
